import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "reactstrap";
import { CheckCircle, Download, FileText, File, Loader2 } from "lucide-react";
import { downloadPdf } from "../../api/downloadPdf";
import { getUser } from "../../api/user";
import { showToast } from "../../utils/showToast";
import { apiBodyMessage } from "../../utils/apiErrorMessage";
import { multipleDownloadZPLFile } from "../../utils/downloadZebra";
import { updatePackagesStatus } from "../../api/packages";
import useAuth from "../../hooks/useAuth";
import fileName from "../../utils/fileName";
import "./ModernConfirmation.scss";

export default function ModernConfirmation({ mssg, packages, setConfirmationModal, fetch }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({});
  const { user } = useAuth();
  const id = user.id;

  // Normalizar packages para asegurar que siempre sea un array
  const normalizedPackages = React.useMemo(() => {
    if (!packages) return [];
    
    // Si es un array, retornarlo directamente
    if (Array.isArray(packages)) {
      // Si el array contiene objetos con estructura { data: {...} }, extraer el data
      return packages.map(pkg => {
        // Si el paquete tiene una propiedad data, usar esa estructura
        if (pkg && typeof pkg === 'object' && pkg.data && typeof pkg.data === 'object') {
          return pkg.data;
        }
        return pkg;
      });
    }
    
    // Si packages es un objeto con una propiedad data que es un array
    if (packages?.data && Array.isArray(packages.data)) {
      return packages.data;
    }
    
    // Si packages es un objeto único (no array), convertirlo a array
    if (packages && typeof packages === 'object') {
      // Si tiene una propiedad data, usar esa
      if (packages.data && typeof packages.data === 'object') {
        return [packages.data];
      }
      // Si no, usar el objeto directamente
      return [packages];
    }
    
    console.warn('ModernConfirmation: packages no es un array válido:', packages);
    return [];
  }, [packages]);

  useEffect(() => {
    getUser({ id })
      .then(({ data: { user } }) => setUserData(user))
      .catch((err) => console.error(`This error ${err}`));
  }, [id]);

  useEffect(() => {
    console.log('ModernConfirmation - packages recibido:', packages);
    console.log('ModernConfirmation - normalizedPackages:', normalizedPackages);
    console.log('ModernConfirmation - cantidad:', normalizedPackages.length);
    if (normalizedPackages.length > 0) {
      console.log('ModernConfirmation - Primer paquete normalizado:', {
        _id: normalizedPackages[0]._id,
        receiver_phone: normalizedPackages[0].receiver_phone,
        receiver_name: normalizedPackages[0].receiver_name,
        type: normalizedPackages[0].type,
        estructura_completa: normalizedPackages[0]
      });
    }
  }, [packages, normalizedPackages]);

  const pdf = async () => {
    setLoading(true);
    try {
      // Validar que normalizedPackages sea un array válido
      if (!normalizedPackages || !Array.isArray(normalizedPackages) || normalizedPackages.length === 0) {
        showToast(t("No hay paquetes para generar el PDF"), false);
        setLoading(false);
        return;
      }
      
      console.log('pdf() - normalizedPackages antes de enviar:', normalizedPackages);
      console.log('pdf() - Primer paquete antes de enviar:', normalizedPackages[0]);

      // downloadPdf ya llama a formatPackagesPDFDownload internamente
      const { data, error } = await downloadPdf({
        clientId: userData?._id || id, // Usar el ID del usuario autenticado o de userData
        index: 0,
        packages: normalizedPackages, // Pasar los paquetes normalizados directamente
      });

      if (data) {
        window.open(data.url);
        setLoading(false);
        setConfirmationModal(false);
        // Actualizar el listado de paquetes después de descargar el PDF
        // ya que el backend actualiza el status de los paquetes
        if (typeof fetch === 'function') {
          fetch(true);
        }
      }

      if (error) {
        showToast(apiBodyMessage(error) ?? t("Error al generar PDF"), false);
        setLoading(false);
      }
    } catch (err) {
      showToast(t("Error al generar PDF"), false);
      setLoading(false);
      console.error("Error downloading PDF:", err);
    }
  };

  const zebra = async () => {
    setLoading(true);
    try {
      // Validar que normalizedPackages sea un array válido
      if (!normalizedPackages || !Array.isArray(normalizedPackages) || normalizedPackages.length === 0) {
        showToast(t("No hay paquetes para generar las etiquetas ZPL"), false);
        setLoading(false);
        return;
      }
      
      // Actualizar el status de los paquetes a 100 (Listo para enviar)
      const updateResponse = await updatePackagesStatus({
        ids: normalizedPackages.map(pkg => pkg._id),
        newStatus: 100
      });

      if (updateResponse.error) {
        console.error("Error updating packages status:", updateResponse.error);
        // Continuar con la descarga aunque haya error en la actualización del status
      }
      
      // Validar que los paquetes tengan la estructura necesaria para ZPL
      const validPackagesForZPL = normalizedPackages.filter(pkg => {
        // Verificar que tenga los campos mínimos necesarios
        return pkg && pkg._id && (pkg.receiver_phone || pkg.receiver_name);
      });

      if (validPackagesForZPL.length === 0) {
        showToast(t("No hay paquetes válidos para generar las etiquetas ZPL"), false);
        setLoading(false);
        return;
      }

      // Generar y descargar el archivo ZPL
      const zplFinal = await multipleDownloadZPLFile({ packages: validPackagesForZPL, user });
      const element = document.createElement("a");
      element.setAttribute(
        "href",
        `data:text/plain;charset=utf-8,${encodeURIComponent(zplFinal)}`
      );
      element.setAttribute("download", fileName("zpl"));
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      setLoading(false);
      setConfirmationModal(false);
      
      // Actualizar el listado de paquetes después de descargar
      if (typeof fetch === 'function') {
        fetch(true);
      }
    } catch (err) {
      showToast(t("Error al generar etiquetas ZPL"), false);
      setLoading(false);
      console.error("Error downloading ZPL:", err);
    }
  };

  const txt = async () => {
    setLoading(true);
    try {
      // Validar que normalizedPackages sea un array válido
      if (!normalizedPackages || !Array.isArray(normalizedPackages) || normalizedPackages.length === 0) {
        showToast(t("No hay paquetes para generar las etiquetas TXT"), false);
        setLoading(false);
        return;
      }
      
      // Actualizar el status de los paquetes a 100 (Listo para enviar)
      const updateResponse = await updatePackagesStatus({
        ids: normalizedPackages.map(pkg => pkg._id),
        newStatus: 100
      });

      if (updateResponse.error) {
        console.error("Error updating packages status:", updateResponse.error);
        // Continuar con la descarga aunque haya error en la actualización del status
      }
      
      // Validar que los paquetes tengan la estructura necesaria para TXT
      const validPackagesForTXT = normalizedPackages.filter(pkg => {
        // Verificar que tenga los campos mínimos necesarios
        return pkg && pkg._id && (pkg.receiver_phone || pkg.receiver_name);
      });

      if (validPackagesForTXT.length === 0) {
        showToast(t("No hay paquetes válidos para generar las etiquetas TXT"), false);
        setLoading(false);
        return;
      }

      // Generar y descargar el archivo TXT
      const zplFinal = await multipleDownloadZPLFile({ packages: validPackagesForTXT, user });
      const element = document.createElement("a");
      element.setAttribute(
        "href",
        `data:text/plain;charset=utf-8,${encodeURIComponent(zplFinal)}`
      );
      element.setAttribute("download", fileName("txt"));
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      setLoading(false);
      setConfirmationModal(false);
      
      // Actualizar el listado de paquetes después de descargar
      if (typeof fetch === 'function') {
        fetch(true);
      }
    } catch (err) {
      showToast(t("Error al generar etiquetas TXT"), false);
      setLoading(false);
      console.error("Error downloading TXT:", err);
    }
  };

  return (
    <div className="modern-confirmation">
      {/* Header con icono y mensaje */}
      <div className="modern-confirmation__header">
        <div className="modern-confirmation__icon">
          <CheckCircle size={48} />
        </div>
        <div className="modern-confirmation__message">
          <h3>{t(mssg)}</h3>
          <p>{t("Se han creado")} {normalizedPackages?.length || 0} {t("paquete")} {normalizedPackages?.length !== 1 ? 's' : ''} {t("exitosamente")}</p>
        </div>
      </div>

      {/* Botones de descarga */}
      <div className="modern-confirmation__actions">
        <div className="modern-confirmation__actions-grid">
          <Button
            className="modern-confirmation__btn modern-confirmation__btn--primary"
            onClick={zebra}
            disabled={loading}
          >
            <Download size={16} />
            <span>{t("Etiquetas ZPL")}</span>
            {loading && <Loader2 size={14} className="modern-confirmation__spinner" />}
          </Button>

          <Button
            className="modern-confirmation__btn modern-confirmation__btn--secondary"
            onClick={pdf}
            disabled={loading}
          >
            <FileText size={16} />
            <span>{t("Etiquetas PDF")}</span>
            {loading && <Loader2 size={14} className="modern-confirmation__spinner" />}
          </Button>

          <Button
            className="modern-confirmation__btn modern-confirmation__btn--tertiary"
            onClick={txt}
            disabled={loading}
          >
            <File size={16} />
            <span>{t("Etiquetas TXT")}</span>
            {loading && <Loader2 size={14} className="modern-confirmation__spinner" />}
          </Button>
        </div>
      </div>

      {/* Opción para hacer más tarde */}
      <div className="modern-confirmation__footer">
        <button
          className="modern-confirmation__later"
          onClick={() => setConfirmationModal(false)}
          disabled={loading}
        >
          {t("También lo puedes hacer más tarde")}
        </button>
      </div>
    </div>
  );
}
